import { CodeCheckListResults } from "src/app/shared/interfaces/code-check-list-results";
import { CodeVerification } from "src/app/shared/interfaces/code-verification";
import { Course } from "src/app/shared/interfaces/course";
import { CourseGroup } from "src/app/shared/interfaces/course-group";
import { Exercise } from "src/app/shared/interfaces/exercise";
import { Highlighter } from "src/app/shared/interfaces/highlighter";
import { Topic } from "src/app/shared/interfaces/topic";
import { Guid } from "src/app/shared/types/guid";
import * as fromCodeCheckHistory from "../actions/code-check-history.actions";
import * as fromQuizHistory from "../actions/quiz-history.actions";
import * as fromCourse from "../actions/course.actions";
import * as fromAuth from "../actions/auth.actions";
import * as fromCourseWS from "../actions/course.websocket.actions";
import * as fromOnboarding from "../actions/onboarding.actions";
import * as fromShortTextExercise from "../actions/exercise-short-text.actions";

import {
  QuizSolutionItem,
  QuizSolutionsListResults,
} from "src/app/modules/quiz/interfaces/quiz-solutions-list-results";
import {
  StatElement,
  StudentStatElement,
} from "src/app/shared/interfaces/student";
import { RequestStatus } from "src/app/shared/interfaces/request-status";
import { StudentShortTextAnswer } from "src/app/shared/interfaces/student-short-text-answer";
import { GradeTextAnswer } from "src/app/shared/interfaces/grade-text-answer";
import * as fromFeedback from "../actions/feedback-course/feedback-course.actions";
import * as fromFeedbackRemind from "../actions/feedback-course/feedback-remind-later.actions";

export interface CourseState {
  course?: CourseGroup | Course;
  currentTopic: Topic;
  activeGroup: Guid;
  stats?: StatElement | null;
  topicStats?: Array<StudentStatElement> | null;
  getCourseFailed: boolean;
  getCourseCompleted: boolean;
  highlighter?: Highlighter | null;
  exercise: Exercise;

  codeCheckHistoryList: CodeCheckListResults;
  getCodeCheckHistoryListCompleted: boolean;
  codeCheckHistoryItem: CodeVerification;
  getCodeCheckHistoryItemCompleted: boolean;

  quizSolutionsList: QuizSolutionsListResults;
  getQuizSolutionsListCompleted: boolean;
  quizSolutionItem: QuizSolutionItem;
  getQuizSolutionItemCompleted: boolean;

  studentShortTextAnswers: Record<Guid, StudentShortTextAnswer[]>;
  teacherShortTextGradeAnswers: Record<Guid, GradeTextAnswer[]>;

  requestStatus?: RequestStatus;
  requestExportStatsStatus?: RequestStatus;
  requestStatStatus?: RequestStatus;
  requestReportGroup?: RequestStatus;
  requestReportIndividual?: RequestStatus;
  requestExerciseStatus?: RequestStatus;
}

export const initialState: CourseState = {
  currentTopic: null,
  getCourseFailed: false,
  getCourseCompleted: false,
  activeGroup: null,
  exercise: null,

  codeCheckHistoryList: null,
  getCodeCheckHistoryListCompleted: false,
  codeCheckHistoryItem: null,
  getCodeCheckHistoryItemCompleted: true,

  quizSolutionsList: null,
  getQuizSolutionsListCompleted: false,
  quizSolutionItem: null,
  getQuizSolutionItemCompleted: true,

  studentShortTextAnswers: null,
  teacherShortTextGradeAnswers: null,

  requestStatus: "IDLE",
  requestExportStatsStatus: "IDLE",
  requestStatStatus: "IDLE",
  requestReportGroup: "IDLE",
  requestReportIndividual: "IDLE",
  requestExerciseStatus: "IDLE",
};

export function courseIdOf(
  course: CourseGroup | Course | null | undefined,
): Guid {
  if (!course) {
    return null;
  }

  return "course" in course ? course.course.id : course.id;
}

export function reducer(
  state = initialState,
  action:
    | fromCourse.CourseActions
    | fromAuth.AuthActions
    | fromCourseWS.CourseWSActions
    | fromCodeCheckHistory.CodeCheckHistoryActions
    | fromQuizHistory.QuizSolutionsActions
    | fromOnboarding.OnboardingActions
    | fromShortTextExercise.ExerciseShortTextActions
    | fromFeedback.FeedbackCourseActions
    | fromFeedbackRemind.FeedbackRemindLaterActions,
): CourseState {
  switch (action.type) {
    case fromAuth.LOGOUT:
    case fromAuth.LOGIN_COMPLETED:
      return initialState;

    case fromCourse.GET_COURSE:
    case fromCourse.GET_COURSE_BY_GROUP: {
      const { CourseId } = action.payload;
      const GroupId =
        action.type === fromCourse.GET_COURSE_BY_GROUP
          ? action.payload.GroupId
          : null;

      const isCached =
        courseIdOf(state.course) === CourseId && state.activeGroup === GroupId;

      return isCached
        ? state
        : {
            ...state,
            course: null,
            getCourseCompleted: false,
          };
    }

    case fromOnboarding.GET_ONBOARDING:
      return {
        ...state,
        course: null,
        getCourseCompleted: false,
      };

    case fromCourse.GET_COURSE_COMPLETED: {
      return {
        ...state,
        course: action.payload,
        getCourseFailed: false,
        getCourseCompleted: true,
      };
    }

    case fromCourse.START_EXERCISE_NONE:
    case fromCourse.START_TEACHER_EXERCISE_NONE: {
      return { ...state, requestStatus: "PENDING" };
    }

    case fromCourse.START_EXERCISE_COMPLETED_NONE: {
      const updatedTopics = state.course.topics.map((topic) => {
        if (topic.id === action.payload.TopicId) {
          const updatedExercises = topic.exercises.map((exercise) => {
            if (exercise.id === action.payload.exercise.id) {
              return {
                ...exercise,
                ...action.payload.exercise,
                available:
                  action.payload.exercise.available ?? exercise.available,
              };
            }
            return exercise;
          });

          return {
            ...topic,
            exercises: updatedExercises,
          };
        }

        return topic;
      });

      let updatedCurrentTopic = state.currentTopic;

      if (state.currentTopic?.id === action.payload.TopicId) {
        const updatedExercises = state.currentTopic.exercises.map((exercise) =>
          exercise.id === action.payload.exercise.id
            ? {
                ...exercise,
                ...action.payload.exercise,
                available:
                  action.payload.exercise.available ?? exercise.available,
              }
            : exercise,
        );

        updatedCurrentTopic = {
          ...state.currentTopic,
          exercises: updatedExercises,
        };
      }

      return {
        ...state,
        course: {
          ...state.course,
          topics: updatedTopics,
        },
        currentTopic: updatedCurrentTopic,
        requestStatus: "SUCCESS",
      };
    }

    case fromCourse.START_EXERCISE_FAILED_NONE: {
      return { ...state, requestStatus: "FAILED" };
    }

    case fromCourse.SET_ACTIVE_GROUP: {
      return {
        ...state,
        activeGroup: action.payload,
      };
    }

    case fromCourse.SET_CURRENT_TOPIC: {
      return {
        ...state,
        currentTopic: action.payload.Topic,
      };
    }

    case fromCourse.GET_COURSE_FAILED: {
      return {
        ...state,
        getCourseFailed: true,
      };
    }

    case fromCourse.GET_COURSE_STATISTICS: {
      return {
        ...state,
        stats: null,
        requestStatStatus: "PENDING",
      };
    }

    case fromCourse.GET_COURSE_STATISTICS_COMPLETED: {
      return {
        ...state,
        stats: action.payload,
        requestStatStatus: "SUCCESS",
      };
    }

    case fromCourse.GET_TOPIC_STATISTICS: {
      return {
        ...state,
        topicStats: null,
      };
    }

    case fromCourse.GET_TOPIC_STATISTICS_COMPLETED: {
      return {
        ...state,
        topicStats: action.payload,
      };
    }

    case fromCourse.GET_EXERCISE: {
      return {
        ...state,
        exercise: null,
        requestExerciseStatus: "PENDING",
      };
    }

    case fromCourse.GET_EXERCISE_COMPLETED: {
      return {
        ...state,
        exercise: action.payload,
        requestExerciseStatus: "SUCCESS",
      };
    }

    case fromCourse.GET_EXERCISE_FAILED: {
      return {
        ...state,
        requestExerciseStatus: "FAILED",
      };
    }

    case fromCourse.GET_EXERCISE_RESTART: {
      return {
        ...state,
        requestExerciseStatus: "IDLE",
      };
    }

    case fromCourse.GET_HELP_COMPLETED: {
      return {
        ...state,
        currentTopic: {
          ...state.currentTopic,
          exercises: state.currentTopic.exercises.map((exercise: Exercise) => {
            return exercise.id === action.payload.ExerciseId
              ? <Exercise>{ ...exercise, help: action.payload.hints }
              : exercise;
          }),
        },
        course: {
          ...state.course,
          topics: state.course.topics.map((topic: Topic) => {
            return topic.id === state.currentTopic.id
              ? {
                  ...topic,
                  exercises: topic.exercises.map((exercise: Exercise) => {
                    return exercise.id === action.payload.ExerciseId
                      ? <Exercise>{ ...exercise, help: action.payload.hints }
                      : exercise;
                  }),
                }
              : topic;
          }),
        },
      };
    }

    case fromCourse.GET_VIDEO_HELP_COMPLETED: {
      return {
        ...state,
        currentTopic: {
          ...state.currentTopic,
          exercises: state.currentTopic.exercises.map((exercise: Exercise) => {
            return exercise.id === action.payload.ExerciseId
              ? <Exercise>{ ...exercise, video_help: action.payload.video }
              : exercise;
          }),
        },
        course: {
          ...state.course,
          topics: state.course.topics.map((topic: Topic) => {
            return topic.id === state.currentTopic.id
              ? {
                  ...topic,
                  exercises: topic.exercises.map((exercise: Exercise) => {
                    return exercise.id === action.payload.ExerciseId
                      ? <Exercise>{
                          ...exercise,
                          video_help: action.payload.video,
                        }
                      : exercise;
                  }),
                }
              : topic;
          }),
        },
      };
    }

    case fromCourse.START_EXERCISE_FAILED: {
      return state.currentTopic
        ? {
            ...state,
            currentTopic: {
              ...state.currentTopic,
              exercises: state.currentTopic.exercises.map(
                (exercise: Exercise) => {
                  return exercise.id === action.payload.ExerciseId
                    ? <Exercise>{ ...exercise, status: "not-started" }
                    : exercise;
                },
              ),
            },
            course: {
              ...state.course,
              topics: state.course.topics.map((topic: Topic) => {
                return topic.id === action.payload.TopicId
                  ? {
                      ...topic,
                      exercises: topic.exercises.map((exercise: Exercise) => {
                        return exercise.id === action.payload.ExerciseId
                          ? <Exercise>{ ...exercise, status: "not-started" }
                          : exercise;
                      }),
                    }
                  : topic;
              }),
            },
          }
        : state;
    }

    case fromCourse.SELECT_UNSELECT_EXERCISE_COMPLETED: {
      return {
        ...state,
        course: {
          ...state.course,
          topics: state.course.topics.map((topic: Topic) => {
            return topic.id === action.payload.TopicId
              ? {
                  ...topic,
                  exercises: topic.exercises.map((exercise: Exercise) => {
                    return exercise.id === action.payload.exercise.id
                      ? action.payload.exercise
                      : exercise;
                  }),
                }
              : topic;
          }),
        },
        currentTopic: {
          ...state.currentTopic,
          exercises: state.currentTopic.exercises.map((exercise: Exercise) => {
            return exercise.id === action.payload.exercise.id
              ? action.payload.exercise
              : exercise;
          }),
        },
      };
    }

    case fromCourse.SELECT_UNSELECT_ALL: {
      return {
        ...state,
        course: {
          ...state.course,
          topics: state.course.topics.map((topic: Topic) => {
            return topic.id === action.payload.selection.TopicId
              ? {
                  ...topic,
                  bulkInProgress: {
                    ...topic.bulkInProgress,
                    [action.payload.selectionParamName]: true,
                  },
                }
              : topic;
          }),
        },
        currentTopic:
          state.currentTopic.id === action.payload.selection.TopicId
            ? {
                ...state.currentTopic,
                bulkInProgress: {
                  ...state.currentTopic.bulkInProgress,
                  section: action.payload.selectionParam.section,
                  [action.payload.selectionParamName]: true,
                },
              }
            : state.currentTopic,
      };
    }

    case fromCourse.SELECT_UNSELECT_ALL_COMPLETED: {
      return {
        ...state,
        course: {
          ...state.course,
          topics: state.course.topics.map((topic: Topic) => {
            return topic.id === action.payload.TopicId
              ? {
                  ...topic,
                  bulkInProgress: {
                    ...topic.bulkInProgress,
                    [action.payload.selectionParamName]: false,
                  },
                  exercises: action.payload.exercises,
                }
              : topic;
          }),
        },
        currentTopic:
          state.currentTopic.id === action.payload.TopicId
            ? {
                ...state.currentTopic,
                bulkInProgress: {
                  ...state.currentTopic.bulkInProgress,
                  [action.payload.selectionParamName]: false,
                },
                exercises: action.payload.exercises,
              }
            : state.currentTopic,
      };
    }

    case fromCourse.START_TEACHER_EXERCISE:
    case fromCourse.START_EXERCISE: {
      return {
        ...state,
        course: {
          ...state.course,
          topics: state.course.topics.map((topic: Topic) => {
            return topic.id === action.payload.TopicId
              ? {
                  ...topic,
                  exercises: topic.exercises.map((exercise: Exercise) => {
                    return exercise.id === action.payload.ExerciseId
                      ? <Exercise>{
                          ...exercise,
                          status: "starting",
                        }
                      : exercise;
                  }),
                }
              : topic;
          }),
        },
        currentTopic: {
          ...state.currentTopic,
          exercises: state.currentTopic.exercises.map((exercise: Exercise) => {
            return exercise.id === action.payload.ExerciseId
              ? <Exercise>{
                  ...exercise,
                  status: "starting",
                }
              : exercise;
          }),
        },
      };
    }

    case fromShortTextExercise.COMMENT_STUDENT_ANSWER:
    case fromShortTextExercise.GRADE_STUDENT_ANSWER:
    case fromShortTextExercise.GRADE_STUDENT_ANSWER_MANUAL: {
      return { ...state, requestStatus: "PENDING" };
    }

    case fromShortTextExercise.GRADE_STUDENT_ANSWER_FAILURE:
    case fromShortTextExercise.GRADE_STUDENT_ANSWER_MANUAL_FAILURE:
    case fromShortTextExercise.ANSWER_TEXT_FAILURE:
    case fromShortTextExercise.COMMENT_STUDENT_ANSWER_FAILURE: {
      return { ...state, requestStatus: "FAILED" };
    }

    case fromShortTextExercise.COMMENT_STUDENT_ANSWER_SUCCESS:
    case fromShortTextExercise.GRADE_STUDENT_ANSWER_SUCCESS: {
      const { exerciseId, gradeTextAnswer } = action.payload;
      const currentAnswers =
        state.teacherShortTextGradeAnswers?.[exerciseId] || [];

      const answerExists = currentAnswers.some(
        (ans) => ans.id === gradeTextAnswer.id,
      );

      let updatedAnswers: GradeTextAnswer[];
      if (answerExists) {
        updatedAnswers = currentAnswers.map((ans) =>
          ans.id === gradeTextAnswer.id ? gradeTextAnswer : ans,
        );
      } else {
        updatedAnswers = [...currentAnswers, gradeTextAnswer];
      }

      return {
        ...state,
        teacherShortTextGradeAnswers: {
          ...state.teacherShortTextGradeAnswers,
          [exerciseId]: updatedAnswers,
        },
        requestStatus: "SUCCESS",
      };
    }

    case fromShortTextExercise.LIST_STUDENT_ANSWERS: {
      return { ...state, requestStatus: "PENDING" };
    }

    case fromShortTextExercise.LIST_STUDENT_ANSWERS_SUCCESS: {
      const { exerciseId, answers } = action.payload;

      return {
        ...state,
        studentShortTextAnswers: {
          ...state.studentShortTextAnswers,
          [exerciseId]: answers,
        },
        requestStatus: "SUCCESS",
      };
    }

    case fromShortTextExercise.LIST_STUDENT_ANSWERS_FAILURE: {
      return {
        ...state,
        requestStatus: "FAILED",
      };
    }

    case fromShortTextExercise.LIST_TEACHER_GRADE_ANSWERS: {
      return { ...state, requestStatus: "PENDING" };
    }

    case fromShortTextExercise.LIST_TEACHER_GRADE_ANSWERS_SUCCESS: {
      return {
        ...state,
        teacherShortTextGradeAnswers: {
          ...state.teacherShortTextGradeAnswers,
          [action.payload.exerciseId]: action.payload.gradeAnswers,
        },
        requestStatus: "SUCCESS",
      };
    }

    case fromShortTextExercise.LIST_TEACHER_GRADE_ANSWERS_FAILURE: {
      return { ...state, requestStatus: "FAILED" };
    }

    case fromCourseWS.ADD_OR_UPDATE_TEACHER_SHORT_TEXT_ANSWER: {
      const { exerciseId, gradeAnswer } = action.payload;
      const currentAnswers =
        state.teacherShortTextGradeAnswers?.[exerciseId] || [];
      const updatedAnswers = currentAnswers.some(
        (ans) => ans.user.id === gradeAnswer.user.id,
      )
        ? currentAnswers.map((ans) =>
            ans.user.id === gradeAnswer.user.id ? gradeAnswer : ans,
          )
        : [...currentAnswers, gradeAnswer];

      return {
        ...state,
        teacherShortTextGradeAnswers: {
          ...state.teacherShortTextGradeAnswers,
          [exerciseId]: updatedAnswers,
        },
      };
    }

    case fromCourseWS.UPDATE_STUDENT_TEXT_ANSWER_REVIEW: {
      const { exerciseShortAnswer, exerciseId, review } = action.payload;
      return {
        ...state,
        studentShortTextAnswers: {
          ...state.studentShortTextAnswers,
          [exerciseId]: (state.studentShortTextAnswers?.[exerciseId] ?? []).map(
            (answer) => {
              if (answer.id === exerciseShortAnswer.short_answer_id) {
                return {
                  ...answer,
                  review,
                  comment: exerciseShortAnswer.comment,
                  stars: exerciseShortAnswer.stars,
                  reviewed_at: exerciseShortAnswer.reviewed_at,
                };
              }
              return answer;
            },
          ),
        },
      };
    }

    case fromShortTextExercise.ANSWER_TEXT: {
      return { ...state, requestStatus: "PENDING" };
    }

    case fromShortTextExercise.ANSWER_TEXT_SUCCESS: {
      const { exerciseId, studentShortTextAnswer } = action.payload;
      const existingAnswers = state.studentShortTextAnswers?.[exerciseId] || [];
      const alreadyExists = existingAnswers.some(
        (a) => a.id === studentShortTextAnswer.id,
      );

      return {
        ...state,
        studentShortTextAnswers: {
          ...state.studentShortTextAnswers,
          [exerciseId]: alreadyExists
            ? existingAnswers.map((a) =>
                a.id === studentShortTextAnswer.id ? studentShortTextAnswer : a,
              )
            : [studentShortTextAnswer, ...existingAnswers],
        },
        requestStatus: "SUCCESS",
      };
    }

    case fromCourse.ANSWER: {
      return {
        ...state,
        requestStatus: "PENDING",
      };
    }

    case fromCourse.START_EXERCISE_COMPLETED:
    case fromCourse.STOP_EXERCISE_COMPLETED: {
      return updateExerciseState(state, action.payload);
    }

    case fromCourse.ANSWER_COMPLETED: {
      return updateExerciseState(state, action.payload, {
        requestStatus: "SUCCESS",
      });
    }

    case fromCourse.ANSWER_COMPLETED_WRONG: {
      return {
        ...state,
        currentTopic: {
          ...state.currentTopic,
          exercises: state.currentTopic.exercises.map((exercise: Exercise) => {
            return exercise.id === action.payload.exercise.id
              ? { ...exercise, passed: false }
              : exercise;
          }),
        },
        requestStatus: "FAILED",
      };
    }

    case fromCourse.DOWNLOAD_GROUP_REPORT: {
      return {
        ...state,
        requestReportGroup: "PENDING",
      };
    }

    case fromCourse.DOWNLOAD_GROUP_REPORT_SUCCESS: {
      return {
        ...state,
        requestReportGroup: "SUCCESS",
      };
    }

    case fromCourse.DOWNLOAD_GROUP_REPORT_FAILED: {
      return {
        ...state,
        requestReportGroup: "FAILED",
      };
    }

    case fromCourse.DOWNLOAD_GROUP_REPORT_RESTART: {
      return {
        ...state,
        requestReportGroup: "IDLE",
      };
    }

    case fromCourse.DOWNLOAD_INDIVIDUAL_REPORT: {
      return {
        ...state,
        requestReportIndividual: "PENDING",
      };
    }

    case fromCourse.DOWNLOAD_INDIVIDUAL_REPORT_SUCCESS: {
      return {
        ...state,
        requestReportIndividual: "SUCCESS",
      };
    }

    case fromCourse.DOWNLOAD_INDIVIDUAL_REPORT_FAILED: {
      return {
        ...state,
        requestReportIndividual: "FAILED",
      };
    }

    case fromCourse.DOWNLOAD_INDIVIDUAL_REPORT_RESTART: {
      return {
        ...state,
        requestReportIndividual: "IDLE",
      };
    }

    case fromCourseWS.UPDATE_TOPIC: {
      return state.currentTopic && state.activeGroup === action.payload.groupId
        ? {
            ...state,
            currentTopic:
              action.payload.id === state.currentTopic.id
                ? { ...state.currentTopic, exercises: action.payload.exercises }
                : state.currentTopic,
            course: {
              ...state.course,
              topics: state.course.topics.map((topic: Topic) => {
                return topic.id === action.payload.id
                  ? { ...topic, exercises: action.payload.exercises }
                  : topic;
              }),
            },
          }
        : state;
    }

    case fromCourseWS.HIGHLIGHT_EXERCISE: {
      return {
        ...state,
        highlighter: action.payload,
      };
    }

    case fromCourseWS.UPDATE_USER_VPN_CONNECTION_STATUS: {
      return state.topicStats
        ? {
            ...state,
            topicStats: state.topicStats.map((stat) =>
              stat.email === action.payload.email
                ? { ...stat, remote_ip: action.payload.remote_ip }
                : stat,
            ),
          }
        : state;
    }

    case fromCodeCheckHistory.GET_CODE_CHECK_LIST: {
      return {
        ...state,
        getCodeCheckHistoryListCompleted: false,
        getCodeCheckHistoryItemCompleted: true,
        codeCheckHistoryItem: null,
      };
    }

    case fromCodeCheckHistory.GET_CODE_CHECK_LIST_COMPLETED: {
      return {
        ...state,
        codeCheckHistoryList: action.payload,
        getCodeCheckHistoryListCompleted: true,
      };
    }

    case fromCodeCheckHistory.GET_CODE_CHECK_LIST_PAGE_COMPLETED: {
      return {
        ...state,
        codeCheckHistoryList: {
          ...action.payload,
          results: state.codeCheckHistoryList.results.concat(
            action.payload.results,
          ),
        },
      };
    }

    case fromCodeCheckHistory.GET_CODE_CHECK: {
      return {
        ...state,
        getCodeCheckHistoryItemCompleted: false,
      };
    }

    case fromCodeCheckHistory.GET_CODE_CHECK_COMPLETED: {
      return {
        ...state,
        codeCheckHistoryItem: action.payload,
        getCodeCheckHistoryItemCompleted: true,
      };
    }

    case fromQuizHistory.GET_QUIZ_SOLUTIONS_LIST: {
      return {
        ...state,
        getQuizSolutionsListCompleted: false,
        getQuizSolutionItemCompleted: true,
        quizSolutionItem: null,
      };
    }

    case fromQuizHistory.GET_QUIZ_SOLUTIONS_LIST_COMPLETED: {
      return {
        ...state,
        quizSolutionsList: action.payload,
        getQuizSolutionsListCompleted: true,
      };
    }

    case fromQuizHistory.GET_QUIZ_SOLUTIONS_LIST_PAGE_COMPLETED: {
      return {
        ...state,
        quizSolutionsList: {
          ...action.payload,
          results: state.quizSolutionsList.results.concat(
            action.payload.results,
          ),
        },
      };
    }

    case fromQuizHistory.GET_QUIZ_SOLUTION: {
      return {
        ...state,
        getQuizSolutionItemCompleted: false,
      };
    }

    case fromQuizHistory.GET_QUIZ_SOLUTION_COMPLETED: {
      return {
        ...state,
        quizSolutionItem: action.payload,
        getQuizSolutionItemCompleted: true,
      };
    }

    case fromCourse.EXPORT_COURSE_STATISTICS_XLSX: {
      return {
        ...state,
        requestExportStatsStatus: "PENDING",
      };
    }

    case fromCourse.EXPORT_COURSE_STATISTICS_XLSX_COMPLETED: {
      return {
        ...state,
        requestExportStatsStatus: "SUCCESS",
      };
    }

    case fromCourse.EXPORT_COURSE_STATISTICS_XLSX_FAILED: {
      return {
        ...state,
        requestExportStatsStatus: "FAILED",
      };
    }

    case fromFeedback.FEEDBACK_COURSE_SUCCESS: {
      return {
        ...state,
        course: {
          ...state.course,
          course_rating: action.feedback.rating_stage,
          rating_remind_later: null,
        },
      };
    }

    case fromFeedbackRemind.FEEDBACK_REMIND_LATER_SUCCESS: {
      return {
        ...state,
        course: {
          ...state.course,
          rating_remind_later: new Date().toISOString(),
        },
      };
    }

    default:
      return state;
  }
}

function updateExerciseState(
  state: CourseState,
  payload: { exercise: Exercise; TopicId: Guid; GroupId?: Guid },
  additionalState: Partial<CourseState> = {},
): CourseState {
  if (
    !state.course ||
    !state.currentTopic ||
    (payload.GroupId && state.activeGroup !== payload.GroupId)
  ) {
    return state;
  }

  return {
    ...state,
    course: {
      ...state.course,
      topics: state.course.topics.map((topic: Topic) =>
        topic.id === payload.TopicId
          ? {
              ...topic,
              exercises: topic.exercises.map((exercise: Exercise) =>
                exercise.id === payload.exercise.id
                  ? {
                      ...exercise,
                      status: payload.exercise.status,
                      passed: payload.exercise.passed,
                      description: payload.exercise.description,
                      mark: payload.exercise.mark,
                      can_start: payload.exercise.can_start,
                    }
                  : exercise,
              ),
            }
          : topic,
      ),
    },
    currentTopic: {
      ...state.currentTopic,
      exercises: state.currentTopic.exercises.map((exercise: Exercise) =>
        exercise.id === payload.exercise.id
          ? {
              ...exercise,
              status: payload.exercise.status,
              passed: payload.exercise.passed,
              description: payload.exercise.description,
              mark: payload.exercise.mark,
              can_start: payload.exercise.can_start,
            }
          : exercise,
      ),
    },
    ...additionalState,
  };
}
